import _ from 'lodash';
import React, {FunctionComponent} from 'react';
import {ImageUploadGui} from '@treez-ui/core/forms/FileUpload/index';
import {ModalType} from '@treez-ui/core/feedback/modals';
import {ImageAttributes} from '@treez/commons/sharedTypings';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {uploadImage} from '~/utils/StoreTreezAPI';

/**
 * Code commented out until next ui/core release later today/tomorrow
 */

interface Props {
    setUploadedImages: Function;
    uploadedImages: ImageAttributes[];
}

export const ImageGui: FunctionComponent<Props> = ({setUploadedImages, uploadedImages}: Props) => {
    const maxFileSize = 1048576;

    const receiveUploadedImages = (images: ImageAttributes[]) => {
        const imagesCopy = [...images];

        if (_.isEmpty(uploadedImages) && !_.isEmpty(imagesCopy)) {
            imagesCopy[0].default = true;
        }

        setUploadedImages(uploadedImages.concat(imagesCopy));
    };

    const uploadImages = async (files) => {
        const uploadPromises = _.map(files, async (file) => {
            try {
                if (file.size >= maxFileSize) {
                    throw new Error(
                        `${file.name} Image too large. Please resize images to less than 1MB.`
                    );
                }

                const result = await uploadImage(file);
                if (_.size(result.data.errors)) {
                    throw new Error(`${file.name}: ${result.data.errors.join('\n')}`);
                }

                return result.data.imageUrl;
            } catch (error) {
                if (error instanceof Error) {
                    return error;
                }
                throw new Error(`${file.name}: Upload returned ${error.response.status}.`);
            }
        });

        const uploadResults = await Promise.all(uploadPromises);
        const [errors, imageUrls] = _.partition(uploadResults, (result) => {
            return result instanceof Error;
        });

        const failCount = errors.length;
        const errorString = errors.join('\n');

        if (failCount > 0) {
            openFeedbackModal(closeFeedbackModal, 'Image Upload Failure', {
                bodyText: `${failCount} of the images could not be uploaded at this time.\n ${errorString}`,
                type: ModalType.WARNING,
            });
        }

        const images: ImageAttributes[] = _.map(imageUrls, (url) => {
            return {url, default: false};
        });

        receiveUploadedImages(images);
    };

    return (
        <ImageUploadGui
            uploadedImages={uploadedImages}
            updateUploadedImages={(newImageList) => {
                setUploadedImages(newImageList);
            }}
            uploadImages={uploadImages}
        />
    );
};

export default ImageGui;
